// V5.26 : Collecte Technique Conditionnelle - Architecture Silos Étanches
// - Filtre actif : N'affiche QUE les catégories où status !== 'none'
// - Zéro décalage : Sport = SportCollector, Presse = PresseCollector
// - Validation souple : pas de messages d'erreur "Échec" pendant la saisie
// - Index Différé : "Je ne connais pas mon numéro" ne bloque pas

import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Flame, Droplets, Wifi, Shield, FileText, Upload, Building2, Heart,
  AlertTriangle, Info, Clock, MessageSquare, ArrowRight, ArrowLeft,
  CheckCircle, Loader2, HelpCircle, Home, Calendar, Dumbbell, Newspaper
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../App';
import { SERVICE_CATEGORIES } from './StrategyStepV526';

// V5.26 : Configuration des collecteurs par catégorie
// V5.49 : PDL/PCE rétablis à l'Étape 3 + Conservation dans post-validation
const CATEGORY_COLLECTORS = {
  // V5.49 / DDD.9 : Électricité — PDL + référence client (homogénéisé Démén/Succession)
  electricite: {
    icon: Zap,
    color: 'amber',
    title: 'Électricité',
    fields: [
      {
        id: 'pdl',
        label: 'Point de Livraison (PDL)',
        placeholder: 'Ex: 01234567890123',
        helpText: 'Trouvable sur votre facture ou compteur Linky (14 chiffres)',
        minLength: 14,
        warningMessage: 'Le PDL devrait contenir au moins 14 chiffres',
      },
      {
        id: 'numContrat',
        label: 'Référence client / N° de contrat',
        placeholder: 'Ex: 1234567890',
        helpText: 'Référence client visible en haut de votre facture',
      }
    ]
  },
  // V5.49 / DDD.9 : Gaz — PCE + référence client
  gaz: {
    icon: Flame,
    color: 'orange',
    title: 'Gaz',
    fields: [
      {
        id: 'pce',
        label: 'Point de Comptage et d\'Estimation (PCE)',
        placeholder: 'Ex: GI012345678901',
        helpText: 'Trouvable sur votre facture de gaz',
        minLength: 14,
        warningMessage: 'Le PCE devrait contenir au moins 14 caractères',
      },
      {
        id: 'numContrat',
        label: 'Référence client / N° de contrat',
        placeholder: 'Ex: 1234567890',
        helpText: 'Référence client visible en haut de votre facture',
      }
    ]
  },
  eau: {
    icon: Droplets,
    color: 'sky',
    title: 'Eau',
    fields: [
      {
        id: 'numContrat',
        label: 'Numéro de contrat Eau',
        placeholder: 'Référence client',
        helpText: 'Trouvable sur votre facture d\'eau',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je ne connais pas mon numéro d\'eau'
      }
    ]
  },
  internet: {
    icon: Wifi,
    color: 'blue',
    title: 'Box Internet & Fibre',
    fields: [
      {
        id: 'rio',
        label: 'Relevé d\'Identité Opérateur (RIO)',
        placeholder: '12 caractères alphanumériques',
        helpText: 'Composez le 3179 depuis votre mobile pour l\'obtenir',
        exactLength: 12,
        errorMessage: 'Le RIO doit contenir exactement 12 caractères alphanumériques',
        validation: /^[A-Z0-9]{12}$/i,
        isCritical: true // Seul champ vraiment bloquant
      }
    ]
  },
  assurance: {
    icon: Shield,
    color: 'purple',
    title: 'Assurance Habitation',
    fields: [
      {
        id: 'numContrat',
        label: 'Numéro de contrat Assurance',
        placeholder: 'Numéro de police',
        helpText: 'Trouvable sur votre attestation d\'assurance',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas le numéro sous la main'
      }
    ]
  },
  mutuelle: {
    icon: Heart,
    color: 'rose',
    title: 'Mutuelle Santé',
    fields: [
      {
        id: 'numAdherent',
        label: 'Numéro d\'adhérent Mutuelle',
        placeholder: 'Numéro d\'adhérent',
        helpText: 'Trouvable sur votre carte mutuelle',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas mon numéro d\'adhérent'
      }
    ]
  },
  banque: {
    icon: Building2,
    color: 'cyan',
    title: 'Banque & Finance',
    isNotification: true, // Juste une notification, pas une résiliation
    fields: [
      {
        id: 'iban',
        label: 'IBAN / Numéro de compte',
        placeholder: 'FR76 xxxx xxxx xxxx xxxx',
        helpText: 'Trouvable sur votre RIB ou relevé de compte',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas mon IBAN'
      }
    ],
    legalNote: 'Ce mandat ordonne uniquement la mise à jour de vos coordonnées postales (Article 16 RGPD).'
  },
  sport: {
    icon: Dumbbell,
    color: 'emerald',
    title: 'Salles de Sport / Fitness',
    fields: [
      {
        id: 'numAdherent',
        label: 'Numéro d\'adhérent',
        placeholder: 'Trouvable sur votre carte ou espace client',
        helpText: 'Sur votre carte de membre ou facture',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas le numéro'
      }
    ]
  },
  presse: {
    icon: Newspaper,
    color: 'slate',
    title: 'Presse & Magazines',
    fields: [
      {
        id: 'numAbonne',
        label: 'Numéro d\'abonné',
        placeholder: 'Trouvable sur le bandeau de livraison',
        helpText: 'Sur l\'étiquette d\'adresse ou espace abonné',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas le numéro d\'abonné'
      }
    ]
  },
  // V1.2 SUCCESSION : catégories spécifiques au tunnel Héritage
  assurance_vie: {
    icon: Shield,
    color: 'purple',
    title: 'Assurance Vie & Prévoyance',
    fields: [
      {
        id: 'numContrat',
        label: 'Numéro du contrat du défunt',
        placeholder: 'Référence du contrat d\'assurance vie',
        helpText: 'Trouvable sur un relevé annuel ou tout document du contrat',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je cherche encore le contrat'
      }
    ]
  },
  energie: {
    icon: Zap,
    color: 'amber',
    title: 'Énergie (Électricité & Gaz)',
    fields: [
      {
        id: 'refClient',
        label: 'Référence client / Numéro de contrat',
        placeholder: 'Ex: 12345678 (sur facture)',
        helpText: 'Trouvable sur une facture EDF / Engie / TotalEnergies du défunt',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je cherche encore les factures'
      },
      {
        id: 'pdl',
        label: 'PDL (Point de Livraison)',
        placeholder: '14 chiffres',
        helpText: 'Sur facture d\'électricité ou compteur Linky',
        minLength: 14,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas le PDL'
      }
    ]
  },
  telecom: {
    icon: Wifi,
    color: 'blue',
    title: 'Internet & Télécom',
    fields: [
      {
        id: 'numContrat',
        label: 'Numéro de contrat / client',
        placeholder: 'Référence facture Orange, Free, SFR, Bouygues…',
        helpText: 'Trouvable sur une facture box ou mobile du défunt',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas la référence'
      }
    ]
  },
  // V1.2 SUCCESSION : catégories supplémentaires réalistes (résiliation courrier)
  auto: {
    icon: FileText,
    color: 'amber',
    title: 'Véhicule (SIV/ANTS)',
    fields: [
      {
        id: 'immatriculation',
        label: 'Numéro d\'immatriculation',
        placeholder: 'AB-123-CD',
        helpText: 'Sur la carte grise du véhicule',
        minLength: 7,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas la carte grise'
      }
    ]
  },
  bail: {
    icon: FileText,
    color: 'blue',
    title: 'Bail de location',
    fields: [
      {
        id: 'refBail',
        label: 'Référence bail / numéro locataire',
        placeholder: 'Sur contrat de bail ou quittance',
        helpText: 'Référence bailleur ou agence de gestion',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas la référence'
      }
    ]
  },
  impots: {
    icon: Building2,
    color: 'red',
    title: 'Impôts (DGFiP)',
    fields: [
      {
        id: 'numFiscal',
        label: 'Numéro fiscal du défunt',
        placeholder: '13 chiffres',
        helpText: 'Sur l\'avis d\'imposition du défunt',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Je cherche l\'avis d\'imposition'
      }
    ]
  },
  caf: {
    icon: Building2,
    color: 'red',
    title: 'CAF',
    fields: [
      {
        id: 'numAllocataire',
        label: 'Numéro allocataire CAF',
        placeholder: '7 chiffres',
        helpText: 'Sur les courriers de la CAF',
        minLength: 3,
        supportsDeferred: true,
        deferredLabel: 'Non applicable'
      }
    ]
  },
  cpam: {
    icon: Heart,
    color: 'red',
    title: 'CPAM',
    fields: [
      {
        id: 'numSecu',
        label: 'Numéro de sécurité sociale du défunt',
        placeholder: '13 chiffres + 2 clé',
        helpText: 'Sur la carte Vitale ou attestation de droits',
        minLength: 13,
        supportsDeferred: true,
        deferredLabel: 'Je n\'ai pas la carte Vitale'
      }
    ]
  }
};

// V5.26 : Composant Champ Technique Simplifié
const TechnicalField = ({
  field,
  value,
  onChange,
  deferredEnabled,
  onDeferredChange,
  mode = 'demenagement',
}) => {
  const [touched, setTouched] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  const hasValue = value && value.length > 0;
  
  // V5.26 : Validation souple - pas de rouge pendant la frappe
  const getValidationState = () => {
    if (!touched || !hasValue) return 'neutral';
    
    if (field.validation) {
      return field.validation.test(value) ? 'valid' : 'invalid';
    }
    
    if (field.exactLength && value.length !== field.exactLength) {
      return 'warning';
    }
    
    if (field.minLength && value.length < field.minLength) {
      return 'warning';
    }
    
    return 'valid';
  };
  
  const validationState = getValidationState();
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-white font-medium text-sm">
          {field.label}
        </label>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="p-1 text-gray-500 hover:text-cyan-400 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>
      
      {/* Aide contextuelle */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-2 bg-cyan-400/10 rounded-lg border border-cyan-400/30 overflow-hidden"
          >
            <p className="text-cyan-400 text-xs flex items-center gap-2">
              <Info className="w-3 h-3" />
              {field.helpText}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Champ de saisie */}
      <div className="relative">
        <Input
          value={value || ''}
          onChange={(e) => {
            // V1.2 FIX bug RIO : valider à la frappe (pas seulement au blur) sinon le
            // champ 12 car. reste "non validé" tant que le client n'a pas cliqué ailleurs.
            setTouched(true);
            onChange(e.target.value.toUpperCase());
          }}
          onBlur={() => setTouched(true)}
          placeholder={field.placeholder}
          disabled={deferredEnabled}
          className={`bg-gray-800/50 pr-10 ${
            validationState === 'valid' ? 'border-green-500/50' :
            validationState === 'invalid' ? 'border-red-500/50' :
            validationState === 'warning' ? 'border-yellow-500/50' :
            'border-gray-700'
          } ${deferredEnabled ? 'opacity-50' : ''}`}
          autoComplete="off"
          data-testid={`field-${field.id}`}
        />
        {touched && hasValue && !deferredEnabled && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {validationState === 'valid' && <CheckCircle className="w-4 h-4 text-green-500" />}
            {validationState === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
            {validationState === 'invalid' && <AlertTriangle className="w-4 h-4 text-red-500" />}
          </div>
        )}
      </div>
      
      {/* Message de warning (pas d'erreur bloquante) */}
      {touched && hasValue && validationState === 'warning' && field.warningMessage && (
        <p className="text-yellow-400 text-xs flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {field.warningMessage}
        </p>
      )}
      
      {/* Message d'erreur critique (RIO uniquement) */}
      {touched && hasValue && validationState === 'invalid' && field.isCritical && (
        <p className="text-red-400 text-xs flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {field.errorMessage}
        </p>
      )}
      
      {/* DDD.8 : option "Je ne connais pas mon X" supprimée — le client laisse vide
          ou utilise "Sauvegarder & reprendre plus tard" en haut du tunnel. */}
    </div>
  );
};

// V5.26 : Composant Collecteur par Catégorie (Dynamique)
const CategoryCollector = ({
  categoryId,
  categoryData,
  provider,
  onDataChange,
  mode = 'demenagement',
}) => {
  // V5.26 FIX: Hooks doivent être appelés avant tout return conditionnel
  const [deferredFields, setDeferredFields] = useState({});
  
  const config = CATEGORY_COLLECTORS[categoryId];
  
  // Early return APRÈS les hooks
  if (!config) return null;
  
  const Icon = config.icon;
  const isDeactivate = categoryData?.status === 'desactive';
  
  const handleFieldChange = (fieldId, value) => {
    onDataChange({
      ...categoryData?.data,
      [fieldId]: value
    });
  };
  
  const handleDeferredChange = (fieldId, enabled) => {
    setDeferredFields(prev => ({ ...prev, [fieldId]: enabled }));
    if (enabled) {
      // Effacer la valeur si on active le différé
      onDataChange({
        ...categoryData?.data,
        [fieldId]: '',
        [`${fieldId}_deferred`]: true
      });
    } else {
      onDataChange({
        ...categoryData?.data,
        [`${fieldId}_deferred`]: false
      });
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-5 rounded-xl border border-${config.color}-500/30 bg-${config.color}-500/5`}
      data-testid={`collector-${categoryId}`}
    >
      {/* Header avec provider */}
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-700/50">
        <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${config.color}-400`} />
        </div>
        <div className="flex-1">
          <h4 className="text-white font-medium">{provider || config.title}</h4>
          <p className="text-gray-500 text-xs">
            {config.isNotification ? 'Notification changement d\'adresse' : 'Informations requises'}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          isDeactivate 
            ? 'bg-red-500/20 text-red-400' 
            : 'bg-emerald-500/20 text-emerald-400'
        }`}>
          {isDeactivate ? 'Je désactive' : 'J\'active'}
        </span>
      </div>
      
      {/* Note légale (Banque) */}
      {config.legalNote && (
        <div className="mb-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
            <p className="text-cyan-400 text-xs">{config.legalNote}</p>
          </div>
        </div>
      )}
      
      {/* Champs techniques */}
      <div className="space-y-4">
        {config.fields.map(field => (
          <TechnicalField
            key={field.id}
            field={field}
            value={categoryData?.data?.[field.id] || ''}
            onChange={(value) => handleFieldChange(field.id, value)}
            deferredEnabled={deferredFields[field.id] || false}
            mode={mode}
            onDeferredChange={(enabled) => handleDeferredChange(field.id, enabled)}
          />
        ))}

        {/* V1.2 : champ libre pour que le client donne du contexte quand il n'a
            pas une donnée (ex: ancien email, adresse utilisée, fournisseur
            précédent...). Envoie tout à l'agent qui pourra s'en servir. */}
        <div className="pt-4 border-t border-gray-700/40">
          <label className="block text-gray-400 text-xs mb-1 font-mono">
            // INFOS_COMPLÉMENTAIRES_POUR_L'EXPERT <span className="text-gray-600 normal-case">(optionnel)</span>
          </label>
          <textarea
            value={categoryData?.data?.agent_notes || ''}
            onChange={(e) => handleFieldChange('agent_notes', e.target.value)}
            placeholder="Ex: compte ouvert au nom de mon ex-conjoint, adresse différente sur la facture, contact téléphone du fournisseur..."
            rows={2}
            className="w-full bg-gray-900/60 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder:text-gray-600 focus:border-cyan-400/60 focus:outline-none resize-none"
          />
          <p className="text-gray-500 text-xs mt-1 italic">
            Ces infos seront transmises à l'expert qui prendra en charge votre résiliation.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// V5.26 : Composant Upload Justificatif de Domicile
// V1.2 : paramétrable via mode='succession' pour afficher "Acte de décès" à la place.
// DDD.10 : ajout d'un mode 'rib' pour réutiliser ce composant pour le RIB bénéficiaire
//          (au lieu d'avoir un mini-bouton custom — le RIB doit avoir le même rendu visuel
//          que l'acte de décès).
const ProofOfAddressUpload = ({
  uploadedDoc,
  onUpload,
  isUploading,
  uploadProgress,
  mode = 'demenagement',
}) => {
  const fileInputRef = useRef(null);
  const isSuccession = mode === 'succession';
  const isRib = mode === 'rib';

  // DDD.10 : configuration unifiée selon le mode
  let title, badge, badgeColor, description, examples, IconComp, iconColor;
  if (isRib) {
    title = 'RIB bénéficiaire (notaire ou ayant droit)';
    badge = 'RECOMMANDÉ';
    badgeColor = 'bg-magenta-500/20 text-magenta-400';
    description = (
      <>Pour que les organismes puissent reverser les éventuels trop-perçus.
      En succession, le virement va généralement au <span className="text-magenta-400 font-medium">notaire</span> en
      charge du dossier, ou à défaut au bénéficiaire identifié.</>
    );
    examples = ['RIB notaire', 'RIB ayant droit', 'IBAN PDF banque'];
    IconComp = FileText;
    iconColor = 'text-magenta-400';
  } else if (isSuccession) {
    title = 'Acte de Décès';
    badge = 'OBLIGATOIRE';
    badgeColor = 'bg-red-500/20 text-red-400';
    description = (
      <>Ce document est <span className="text-magenta-400 font-medium">indispensable</span> pour
      déclencher toutes les démarches de résiliation auprès des organismes du défunt.</>
    );
    examples = ['Acte de décès (mairie)', 'Copie intégrale'];
    IconComp = Home;
    iconColor = 'text-yellow-400';
  } else {
    title = 'Justificatif de Domicile';
    badge = 'RECOMMANDÉ';
    badgeColor = 'bg-red-500/20 text-red-400';
    description = (
      <>Ce document servira de <span className="text-cyan-400 font-medium">clé de voûte juridique</span> pour
      vos démarches. Il sera virtuellement rattaché à chaque notification.</>
    );
    examples = ['Bail de location', 'Titre de propriété', 'Attestation d\'hébergement', 'Facture récente'];
    IconComp = Home;
    iconColor = 'text-yellow-400';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-6 rounded-xl border-2 ${
        uploadedDoc
          ? 'border-green-500/50 bg-green-500/5'
          : isRib
            ? 'border-dashed border-magenta-400/50 bg-magenta-400/5'
            : 'border-dashed border-yellow-400/50 bg-yellow-400/5'
      }`}
      data-testid={`proof-of-address-upload-${mode}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl ${
          uploadedDoc
            ? 'bg-green-500/20'
            : isRib
              ? 'bg-magenta-400/20'
              : 'bg-yellow-400/20'
        } flex items-center justify-center flex-shrink-0`}>
          <IconComp className={`w-7 h-7 ${uploadedDoc ? 'text-green-500' : iconColor}`} />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-white font-bold text-lg">{title}</h3>
            <span className={`px-2 py-0.5 ${badgeColor} text-xs rounded font-mono`}>
              {badge}
            </span>
          </div>

          <p className="text-gray-400 text-sm mb-4">{description}</p>

          <div className="space-y-2 mb-4">
            <div className="flex flex-wrap gap-2">
              {examples.map((ex, idx) => (
                <span key={idx} className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded">{ex}</span>
              ))}
            </div>
          </div>
          
          {uploadedDoc ? (
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div>
                  <p className="text-white font-medium">{uploadedDoc.name}</p>
                  <p className="text-gray-500 text-xs">
                    Uploadé le {new Date(uploadedDoc.uploaded_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-400 hover:text-white text-sm"
              >
                Remplacer
              </button>
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={onUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload en cours... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {isSuccession ? "Uploader l'acte de décès" : 'Uploader le justificatif'}
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Barre de progression */}
          {isUploading && uploadProgress > 0 && (
            <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-yellow-400"
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Note de sécurité */}
      <div className="mt-4 pt-4 border-t border-gray-700/50 flex items-center gap-2">
        <Shield className="w-4 h-4 text-cyan-400" />
        <p className="text-gray-500 text-xs">
          Document chiffré AES-256 et automatiquement purgé après traitement (Protocole Zéro-Trace)
        </p>
      </div>
    </motion.div>
  );
};

// V5.26 : Composant Principal - Collecte Technique
// V1.2 : ajout d'un mode 'succession' qui remplace "Justificatif de domicile" par
// "Acte de décès" et ajoute un upload "RIB du bénéficiaire" optionnel.
const TechnicalCollectionStepV526 = ({
  missionData,
  onMissionDataChange,
  onNext,
  onBack,
  mode = 'demenagement',
}) => {
  const isSuccession = mode === 'succession';
  const proofDocType = isSuccession ? 'acte_deces' : 'justificatif_domicile';
  const proofMissionKey = isSuccession ? 'acte_deces' : 'justificatif_domicile';

  const [uploadedProof, setUploadedProof] = useState(missionData?.[proofMissionKey] || null);
  const [uploadedRib, setUploadedRib] = useState(missionData?.rib_beneficiaire || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingRib, setIsUploadingRib] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // V5.26 : FILTRE ACTIF - N'afficher QUE les catégories où status !== 'none'
  const getActiveCategories = useCallback(() => {
    if (!missionData?.categories) return [];
    
    const active = [];
    Object.entries(missionData.categories).forEach(([categoryId, categoryData]) => {
      if (categoryData?.status && categoryData.status !== 'none') {
        active.push({
          id: categoryId,
          ...categoryData
        });
      }
    });
    
    return active;
  }, [missionData?.categories]);
  
  const activeCategories = getActiveCategories();
  
  // Handler de mise à jour des données techniques
  const handleCategoryDataChange = useCallback((categoryId, newData) => {
    const updatedCategories = {
      ...missionData.categories,
      [categoryId]: {
        ...missionData.categories[categoryId],
        data: newData
      }
    };
    
    onMissionDataChange({
      ...missionData,
      categories: updatedCategories
    });
  }, [missionData, onMissionDataChange]);
  
  // Upload du justificatif de domicile
  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    // V1.2 : doc_type dépend du mode (justificatif domicile pour déménagement,
    // acte de décès pour succession).
    formDataUpload.append('doc_type', proofDocType);
    if (missionData?.id) {
      formDataUpload.append('mission_id', missionData.id);
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/documents/upload`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      if (res.data.document_id) {
        const docInfo = {
          id: res.data.document_id,
          name: file.name,
          uploaded_at: new Date().toISOString()
        };
        setUploadedProof(docInfo);

        // V1.2 : on écrit dans la bonne clé de missionData selon le mode
        onMissionDataChange({
          ...missionData,
          [proofMissionKey]: docInfo
        });

        toast.success(isSuccession ? 'Acte de décès uploadé avec succès !' : 'Justificatif uploadé avec succès !');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
    }
  };

  // V1.2 : Upload du RIB bénéficiaire (succession uniquement, optionnel)
  const handleRibUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingRib(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', 'rib_beneficiaire');
    if (missionData?.id) fd.append('mission_id', missionData.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/documents/upload`, fd, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      if (res.data.document_id) {
        const info = { id: res.data.document_id, name: file.name, uploaded_at: new Date().toISOString() };
        setUploadedRib(info);
        onMissionDataChange({ ...missionData, rib_beneficiaire: info });
        toast.success('RIB du bénéficiaire uploadé !');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload RIB');
    } finally {
      setIsUploadingRib(false);
    }
  };
  
  // V5.26 : Validation pour continuer - seul le RIO est critique
  const canProceed = () => {
    // Vérifier le RIO si Internet est sélectionné
    const internetCategory = missionData?.categories?.internet;
    if (internetCategory?.status && internetCategory.status !== 'none') {
      const rio = internetCategory.data?.rio || '';
      if (rio && !/^[A-Z0-9]{12}$/i.test(rio)) {
        return false;
      }
    }
    return true;
  };
  
  // Handler continuer avec warnings
  const handleContinue = () => {
    // Vérifier le RIO (seule validation critique)
    const internetCategory = missionData?.categories?.internet;
    if (internetCategory?.status && internetCategory.status !== 'none') {
      const rio = internetCategory.data?.rio || '';
      if (rio && !/^[A-Z0-9]{12}$/i.test(rio)) {
        toast.error('Le RIO doit contenir exactement 12 caractères alphanumériques');
        return;
      }
    }
    
    // Warnings pour les champs optionnels manquants
    const warnings = [];
    
    activeCategories.forEach(cat => {
      const config = CATEGORY_COLLECTORS[cat.id];
      if (!config) return;
      
      config.fields.forEach(field => {
        const value = cat.data?.[field.id] || '';
        if (!value && !cat.data?.[`${field.id}_deferred`]) {
          // Champ vide, mais pas bloquant
        }
      });
    });
    
    if (!uploadedProof) {
      toast.warning('Justificatif non uploadé - "Non communiqué" sera affiché dans le PDF');
    }
    
    onNext?.();
  };
  
  // Compteurs
  const completedCount = activeCategories.filter(cat => {
    const config = CATEGORY_COLLECTORS[cat.id];
    if (!config) return false;
    return config.fields.every(field => 
      cat.data?.[field.id] || cat.data?.[`${field.id}_deferred`]
    );
  }).length;
  
  return (
    <div className="space-y-6">
      {/* En-tête */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/20 text-cyan-400 mb-4">
          <FileText className="w-5 h-5" />
          <span className="font-mono text-sm">COLLECTE TECHNIQUE</span>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Informations techniques
        </h2>
        <p className="text-gray-400 max-w-lg mx-auto">
          Ces informations permettent d'identifier vos contrats auprès des fournisseurs.
          Les champs vides afficheront "Non communiqué" dans le mandat.
        </p>
      </motion.div>

      {/* DDD.2 — Bandeau "Compléter plus tard" : étape entièrement optionnelle */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-xl border border-cyan-500/40 bg-gradient-to-r from-cyan-500/10 to-magenta-500/10"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <p className="text-white font-bold text-sm mb-1">
              Pas tout sous la main ? Aucun souci.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              Tous les champs sont <span className="text-cyan-400 font-medium">optionnels</span>. Vous pouvez démarrer la mission immédiatement
              et compléter vos identifiants (numéros de contrat, IBAN, RIO…) à tout moment depuis
              {' '}<span className="text-magenta-400 font-medium">Mes Missions &gt; Détails de la mission</span>.
              Notre équipe vous relancera si une information bloque la résiliation d'un fournisseur précis.
            </p>
          </div>
        </div>
      </motion.div>
      
      {/* Badge de progression */}
      {activeCategories.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-xl border border-gray-700 bg-gray-800/30"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${
                completedCount === activeCategories.length ? 'bg-green-500/20' : 'bg-gray-700'
              } flex items-center justify-center`}>
                <CheckCircle className={`w-5 h-5 ${
                  completedCount === activeCategories.length ? 'text-green-500' : 'text-gray-500'
                }`} />
              </div>
              <div>
                <p className="text-white font-medium">
                  {activeCategories.length} catégorie{activeCategories.length > 1 ? 's' : ''} à renseigner
                </p>
                <p className="text-gray-500 text-xs">
                  {completedCount} complète{completedCount > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Upload Justificatif de Domicile (ou Acte de décès en mode succession) */}
      <ProofOfAddressUpload
        uploadedDoc={uploadedProof}
        onUpload={handleProofUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        mode={mode}
      />

      {/* DDD.10 : RIB bénéficiaire utilise désormais le MÊME composant que l'acte de décès,
          en mode 'rib' (drag-drop card harmonisée, magenta L'Effaceur). Avant : mini bouton
          isolé qui dénotait visuellement avec l'acte de décès juste au-dessus. */}
      {isSuccession && (
        <ProofOfAddressUpload
          uploadedDoc={uploadedRib}
          onUpload={handleRibUpload}
          isUploading={isUploadingRib}
          uploadProgress={0}
          mode="rib"
        />
      )}

      {/* V5.49 : Collecteurs dynamiques - TOUS les collecteurs actifs */}
      {activeCategories.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-gray-400 text-sm font-mono border-b border-gray-700 pb-2">
            // DONNÉES_TECHNIQUES_PAR_FOURNISSEUR
          </h3>
          
          {/* V5.49 : Afficher tous les collecteurs actifs */}
          {activeCategories.map((category, index) => (
            <CategoryCollector
              key={category.id}
              categoryId={category.id}
              categoryData={category}
              provider={category.provider}
              mode={mode}
              onDataChange={(newData) => handleCategoryDataChange(category.id, newData)}
            />
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 rounded-xl border border-gray-700 text-center"
        >
          <Info className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <p className="text-white font-medium mb-2">Aucune catégorie sélectionnée</p>
          <p className="text-gray-500 text-sm">
            Retournez à l'étape précédente pour sélectionner au moins une catégorie.
          </p>
        </motion.div>
      )}
      
      {/* V5.49 : Note d'information sur PDL/PCE - Possibilité de compléter plus tard */}
      {activeCategories.some(cat => cat.id === 'electricite' || cat.id === 'gaz') && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl border border-cyan-400/30 bg-cyan-400/5"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-cyan-400 font-medium text-sm mb-1">PDL & PCE</h4>
              <p className="text-gray-400 text-sm">
                Vous pouvez renseigner ces références maintenant ou <span className="text-white">après validation</span> de votre dossier, 
                depuis la page "Gérer mon dossier". Les champs vides afficheront "Non communiqué" dans le mandat.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* V5.66 : Champ Notes masqué à l'étape 3 — visible uniquement à l'étape 4 (Synthèse) */}
      {/* Notes déplacées dans la Synthèse */}
      
      {/* Note légale */}
      <div className="flex items-start gap-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <p className="text-gray-400 text-xs">
          La qualité des données techniques garantit le succès des notifications légales.
          Les champs non renseignés afficheront "Non communiqué" dans le mandat PDF.
        </p>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-gray-600 text-gray-400"
          data-testid="technical-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        
        <Button
          onClick={handleContinue}
          disabled={!canProceed()}
          className={`${canProceed() ? 'bg-cyan-400 text-black hover:bg-cyan-300' : 'bg-gray-700 text-gray-500'}`}
          data-testid="technical-continue-btn"
        >
          Continuer vers le Mandat
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default TechnicalCollectionStepV526;
